import {
    Component,
    OnInit,
    OnDestroy,
    signal,
    inject,
    DestroyRef
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { changei18n } from '@infragistics/igniteui-angular';
import { interval, Subscription } from 'rxjs';

import { i18nDE } from './core/utils/language-resources/ignite-ui-grid.de';
import { AuthService } from './services/auth.service';
import { LoggerService } from './services/logger.service';
import { SyncService } from './services/sync.service';
import { OnlineStatusService } from './services/online-status.service';
import { concatMap } from 'rxjs/operators';
import { SwUpdate } from '@angular/service-worker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    private syncService = inject(SyncService);
    private onlineStatusService = inject(OnlineStatusService);
    private swUpdate = inject(SwUpdate);
    private destroyRef = inject(DestroyRef);
    private logger = inject(LoggerService);

    canUseSignals = signal(true);
    isLoading = false;

    private onlineSub: Subscription;
    private forceFullSyncSub: Subscription;

    constructor() {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(event => {
                    if (event.type === 'VERSION_READY') {
                        this.applyAppUpdate();
                    }
                });
        }
    }

    ngOnInit(): void {
        const onlineInterval = interval(10000);

        // Check if full sync is needed every hour 1000 * 60 * 60
        const forceFullSyncInterval = interval(1000 * 60);

        this.syncService.start();
        // Pass only the translated keys: changei18n merges partial maps and
        // igniteui falls back to English for anything missing. Copying every
        // GridResourceStringsEN key from i18nDE (as done before) turned the 90
        // keys without a German translation into `undefined`, which crashed
        // grid templates (IgxStringReplacePipe on undefined) during change
        // detection and left every igx grid rendered as an empty shell.
        changei18n(i18nDE);

        this.onlineSub = onlineInterval
            .pipe(concatMap(() => this.onlineStatusService.checkOnlineStatus()))
            .subscribe();

        this.forceFullSyncSub = forceFullSyncInterval
            .pipe(concatMap(() => this.syncService.checkFullSyncNeeded()))
            .subscribe();

        if (this.authService.isLoggedIn()) {
            this.syncService.validateSyncRequirement();
            this.syncService.sync(); // Force sync on every app load to account for operations done after login
        }
    }

    ngOnDestroy(): void {
        this.onlineSub.unsubscribe();
        this.forceFullSyncSub.unsubscribe();
    }

    private async applyAppUpdate(): Promise<void> {
        try {
            const activated = await this.swUpdate.activateUpdate();
            if (activated) {
                window.location.reload();
            }
        } catch (error) {
            this.logger.error('Error while updating the app', error);
            // Optionally reload anyway to ensure user gets the update
            window.location.reload();
        }
    }
}
